import React, { useEffect, useState } from 'react';
import { bool, arrayOf, number, shape, string, object } from 'prop-types';
import { compose } from 'redux';
import { connect } from 'react-redux';
import { useHistory, useLocation } from 'react-router-dom';

import { useConfiguration } from '../../context/configurationContext';
import { useRouteConfiguration } from '../../context/routeConfigurationContext';
import { useMyContextFunctions } from '../../context/ContextFunctions';
import { signsInWithModal } from '../../util/authPrompt';
import { injectIntl, intlShape } from '../../util/reactIntl';
import { propTypes } from '../../util/types';
import {
  ensureCurrentUser,
  ensureUser,
  profileSlug,
  userDisplayNameAsString,
} from '../../util/data';
import { parse, createSlug } from '../../util/urlHelpers';
import { createResourceLocatorString } from '../../util/routes';
import { withViewport } from '../../util/uiHelpers';
import { isScrollingDisabled } from '../../ducks/ui.duck';
import { getMarketplaceEntities } from '../../ducks/marketplaceData.duck';
import { getListingCategories } from '../../ducks/hostedAssets.duck';
import {
  extractYouTubeVideoId,
  isYouTubeUrl,
} from '../../components/FieldRichTextInput/nodes/YouTubeEmbedNode';

import { Page, LayoutSingleColumn, Icons, InlineTextButton } from '../../components';

import TopbarContainer from '../../containers/TopbarContainer/TopbarContainer';
import FooterContainer from '../../containers/FooterContainer/FooterContainer';
import NotFoundPage from '../../containers/NotFoundPage/NotFoundPage';

import ProfileHeader from './ProfileHeader';
import ProfileTabs from './ProfileTabs';
import ListingsTabContent from './ListingsTabContent';
import FeedbackTabContent from './FeedbackTabContent';

import css from './ProfilePage.module.css';

const ProfilePageComponent = props => {
  const {
    scrollingDisabled,
    currentUser,
    userShowError,
    queryListingsInProgress,
    queryListingsError,
    queryReviewsInProgress,
    queryReviewsError,
    user,
    listings,
    reviews,
    intl,
    viewport,
    listingCategories,
    filterCounts,
    listingsPagination,
    vimeoThumbnailUrl,
    params: routeParams,
  } = props;

  const config = useConfiguration();
  const routeConfiguration = useRouteConfiguration();
  const history = useHistory();
  const location = useLocation();
  // Desktop asks a guest to sign in in the modal, over this page
  const { onRequireAuth } = useMyContextFunctions();

  // Parse URL params for tab and review type
  const searchParams = parse(location.search);
  const activeTab = searchParams.tab || 'listings';
  const activeReviewType = searchParams.reviewType || 'all';
  const urlKeywords = searchParams.keywords || '';

  // Local state for review type (to avoid URL flicker)
  const [reviewType, setReviewType] = useState(activeReviewType);

  // Local state for keyword search
  const [keywordInput, setKeywordInput] = useState(urlKeywords);
  // Follow the address: "Clear filters" and the back button empty or refill the field
  useEffect(() => {
    setKeywordInput(urlKeywords);
  }, [urlKeywords]);

  const ensuredCurrentUser = ensureCurrentUser(currentUser);
  const profileUser = ensureUser(user);
  const isCurrentUser =
    ensuredCurrentUser.id && profileUser.id && ensuredCurrentUser.id.uuid === profileUser.id.uuid;
  const displayName = userDisplayNameAsString(profileUser, '');
  // Until the user loads, keep the slug from the URL so links stay valid.
  const username = profileSlug(profileUser) || routeParams?.username;

  const schemaTitleVars = { name: displayName, marketplaceName: config.marketplaceName };
  const schemaTitle = intl.formatMessage({ id: 'ProfilePage.schemaTitle' }, schemaTitleVars);

  // OG image: shopPhotoUrl → YouTube thumbnail → first listing image
  const { bio: rawBio, publicData } = profileUser?.attributes?.profile || {};
  const shopPhotoUrl = publicData?.shopPhotoUrl;
  const shopVideoUrl = publicData?.shopVideoUrl;
  const youtubeOgThumbnail =
    shopVideoUrl && isYouTubeUrl(shopVideoUrl)
      ? `https://img.youtube.com/vi/${extractYouTubeVideoId(shopVideoUrl)}/maxresdefault.jpg`
      : null;
  const firstListingImageUrl =
    listings?.[0]?.images?.[0]?.attributes?.variants?.['listing-card']?.url || null;
  const ogImageUrl =
    shopPhotoUrl || youtubeOgThumbnail || vimeoThumbnailUrl || firstListingImageUrl;
  const ogImages = ogImageUrl
    ? [
        {
          url: ogImageUrl,
          ...(youtubeOgThumbnail && !shopPhotoUrl ? { width: 1280, height: 720 } : {}),
        },
      ]
    : [];

  // Truncated bio for meta description (~155 chars); rawBio used in schema for full text
  const bio =
    rawBio && rawBio.length > 155
      ? rawBio.substring(0, rawBio.lastIndexOf(' ', 155)) + '...'
      : rawBio || '';

  // Schema prep
  const profileUrl = `${config.marketplaceRootURL}/shop/${username}`;
  const locationData = publicData?.location;
  const addressMaybe = locationData?.country
    ? {
        address: {
          '@type': 'PostalAddress',
          addressCountry: locationData.country,
          ...(locationData.state ? { addressRegion: locationData.state } : {}),
          ...(locationData.city ? { addressLocality: locationData.city } : {}),
        },
      }
    : {};
  const providerReviews = (reviews || []).filter(
    r => r.attributes?.type === 'ofProvider' && r.attributes?.rating
  );
  const aggregateRatingMaybe =
    providerReviews.length > 0
      ? {
          aggregateRating: {
            '@type': 'AggregateRating',
            ratingValue: (
              providerReviews.reduce((sum, r) => sum + r.attributes.rating, 0) /
              providerReviews.length
            ).toFixed(1),
            reviewCount: providerReviews.length,
          },
        }
      : {};

  // Handle tab change
  const handleTabChange = tab => {
    const params = tab === 'feedback' ? { tab, reviewType } : { tab };
    history.push(
      createResourceLocatorString('ProfilePage', routeConfiguration, { username }, params)
    );
  };

  // Handle review type change
  const handleReviewTypeChange = type => {
    setReviewType(type);
  };

  // Handle keyword search
  const handleKeywordSubmit = e => {
    e.preventDefault();
    // New words start from the first page
    const { tab, reviewType: rt, keywords, page, ...otherParams } = searchParams;
    const finalParams = {
      ...otherParams,
      tab: 'listings',
      ...(keywordInput ? { keywords: keywordInput } : {}),
    };
    history.push(
      createResourceLocatorString('ProfilePage', routeConfiguration, { username }, finalParams)
    );
  };

  if (userShowError && userShowError.status === 404) {
    return <NotFoundPage staticContext={props.staticContext} />;
  }

  return (
    <Page
      scrollingDisabled={scrollingDisabled}
      title={schemaTitle}
      description={bio}
      author={displayName}
      facebookImages={ogImages}
      twitterImages={ogImages}
      schema={{
        '@context': 'http://schema.org',
        '@type': 'ProfilePage',
        name: schemaTitle,
        url: profileUrl,
        description: rawBio || '',
        ...(ogImageUrl ? { image: ogImageUrl } : {}),
        dateCreated: profileUser?.attributes?.createdAt,
        mainEntity: {
          '@type': 'Person',
          name: displayName,
          url: profileUrl,
          description: rawBio || '',
          ...(ogImageUrl ? { image: ogImageUrl } : {}),
          ...addressMaybe,
          ...aggregateRatingMaybe,
        },
      }}
    >
      <LayoutSingleColumn
        topbar={<TopbarContainer currentPage="ProfilePage" />}
        footer={<FooterContainer />}
      >
        <div className={css.contentWrapper}>
          {/* Profile Header */}
          <ProfileHeader
            user={profileUser}
            isCurrentUser={isCurrentUser}
            listingCount={listings.length}
            vimeoThumbnailUrl={vimeoThumbnailUrl}
            viewport={viewport}
            onContactUser={
              /* TODO: revert */ !isCurrentUser && listings.length > 0
                ? () => {
                    const firstListing = listings[0];
                    // The listing page opens its message window for ?inquiry=true
                    const inquiryPath = `${createResourceLocatorString(
                      'ListingPage',
                      routeConfiguration,
                      { slug: createSlug(firstListing.attributes.title), id: firstListing.id.uuid }
                    )}?inquiry=true`;
                    if (!ensuredCurrentUser.id && signsInWithModal()) {
                      // Desktop: the sign-in modal here, then the message window
                      onRequireAuth({ to: inquiryPath });
                    } else if (!ensuredCurrentUser.id) {
                      const state = {
                        from: `${location.pathname}${location.search}${location.hash}`,
                      };
                      history.push(
                        createResourceLocatorString('SignupPage', routeConfiguration, {}, {}),
                        state
                      );
                    } else {
                      history.push(inquiryPath);
                    }
                  }
                : null
            }
          />

          {/* Tabs and Search */}
          <div className={css.tabsAndSearch}>
            <ProfileTabs
              activeTab={activeTab}
              onTabChange={handleTabChange}
              reviews={reviews}
              listingsCount={listingsPagination?.totalItems ?? listings.length}
            />

            {/* Search form - only show on listings tab */}
            {activeTab === 'listings' && (
              <form className={css.searchForm} onSubmit={handleKeywordSubmit}>
                <div className={css.searchInputWrapper}>
                  <input
                    type="text"
                    className={css.searchInput}
                    placeholder={intl.formatMessage({ id: 'ListingsTabContent.searchPlaceholder' })}
                    aria-label={intl.formatMessage({ id: 'ListingsTabContent.searchPlaceholder' })}
                    value={keywordInput}
                    onChange={e => setKeywordInput(e.target.value)}
                    onFocus={e => {
                      setTimeout(() => {
                        e.target.scrollIntoView({ behavior: 'smooth', block: 'center' });
                      }, 300);
                    }}
                  />
                  <InlineTextButton type="submit" className={css.searchButton}>
                    <Icons name="search" />
                  </InlineTextButton>
                </div>
              </form>
            )}
          </div>

          {/* Tab Content */}
          {activeTab === 'listings' ? (
            <ListingsTabContent
              listings={listings}
              queryListingsInProgress={queryListingsInProgress}
              queryListingsError={queryListingsError}
              pagination={listingsPagination}
              config={config}
              listingCategories={listingCategories}
              location={location}
              history={history}
              routeConfiguration={routeConfiguration}
              username={username}
              filterCounts={filterCounts}
            />
          ) : (
            <FeedbackTabContent
              reviews={reviews}
              queryReviewsInProgress={queryReviewsInProgress}
              queryReviewsError={queryReviewsError}
              activeReviewType={reviewType}
              onReviewTypeChange={handleReviewTypeChange}
              username={username}
              routeConfiguration={routeConfiguration}
            />
          )}
        </div>
      </LayoutSingleColumn>
    </Page>
  );
};

ProfilePageComponent.defaultProps = {
  currentUser: null,
  user: null,
  userShowError: null,
  queryListingsInProgress: false,
  queryListingsError: null,
  reviews: [],
  queryReviewsInProgress: false,
  queryReviewsError: null,
  listingCategories: [],
  filterCounts: {},
  vimeoThumbnailUrl: null,
};

ProfilePageComponent.propTypes = {
  scrollingDisabled: bool.isRequired,
  currentUser: propTypes.currentUser,
  user: propTypes.user,
  userShowError: propTypes.error,
  queryListingsInProgress: bool,
  queryListingsError: propTypes.error,
  listings: arrayOf(propTypes.listing).isRequired,
  reviews: arrayOf(propTypes.review),
  queryReviewsInProgress: bool,
  queryReviewsError: propTypes.error,
  listingCategories: arrayOf(object),
  filterCounts: object,
  vimeoThumbnailUrl: string,

  // from withViewport
  viewport: shape({
    width: number.isRequired,
    height: number.isRequired,
  }).isRequired,

  // from injectIntl
  intl: intlShape.isRequired,
};

const mapStateToProps = state => {
  const { currentUser } = state.user;
  const {
    userId,
    userShowError,
    queryListingsInProgress,
    queryListingsError,
    userListingRefs,
    reviews,
    queryReviewsInProgress,
    queryReviewsError,
    filterCounts,
    listingsPagination,
    vimeoThumbnailUrl,
  } = state.ProfilePage;
  const userMatches = getMarketplaceEntities(state, [{ type: 'user', id: userId }]);
  const user = userMatches.length === 1 ? userMatches[0] : null;
  const listings = getMarketplaceEntities(state, userListingRefs);
  return {
    scrollingDisabled: isScrollingDisabled(state),
    currentUser,
    user,
    userShowError,
    queryListingsInProgress,
    queryListingsError,
    listings,
    reviews,
    queryReviewsInProgress,
    queryReviewsError,
    listingCategories: getListingCategories(state),
    filterCounts,
    listingsPagination,
    vimeoThumbnailUrl,
  };
};

const ProfilePage = compose(
  connect(mapStateToProps),
  withViewport,
  injectIntl
)(ProfilePageComponent);

export default ProfilePage;
